/**
 * Optimiciti API Specification
 * Welcome to the Beta release of the Optimiciti API. This API allows registered users to make direct calls to our optimization solvers via a web request.  Use of the API requires acceptance of our terms of service.  Some useful links: - [Optimiciti Homepage](https://optimiciti.com) - [Optimiciti API Access Plans](https://optimiciti.com/pricing)
 *
 * The version of the OpenAPI document: 0.0.2
 * Contact: api@optimiciti.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 *
 */

import ApiClient from '../ApiClient';
import CostAdder from './CostAdder';
import CostMultiplier from './CostMultiplier';

/**
 * The CostSettings model module.
 * @module model/CostSettings
 * @version 0.0.2
 */
class CostSettings {
    /**
     * Constructs a new <code>CostSettings</code>.
     * This schema defines the settings to use for calculating travel costs between locations. It includes the cost metric (duration or distance), cost multiplier, cost adder, and methods for calculating distances and durations.
     * @alias module:model/CostSettings
     * @param costMetric {module:model/CostSettings.CostMetricEnum} The metric to use as the base for calculating travel costs. A multiplier and/or adder can be applied to this metric to calculate the final travel cost. If duration is selected, then the travel cost will be calculated based on the travel duration between locations. If distance is selected, then the travel cost will be calculated based on the travel distance between locations.
     */
    constructor(costMetric) { 
        
        CostSettings.initialize(this, costMetric);
    }

    /**
     * Initializes the fields of this object.
     * This method is used by the constructors of any subclasses, in order to implement multiple inheritance (mix-ins).
     * Only for internal use.
     */
    static initialize(obj, costMetric) { 
        obj['costMetric'] = costMetric || 'duration';
        obj['autoCalculateDuration'] = false;
        obj['autoCalculateDistance'] = false;
        obj['distanceMethod'] = 'geo';
        obj['durationMethod'] = 'geo';
    }

    /**
     * Constructs a <code>CostSettings</code> from a plain JavaScript object, optionally creating a new instance.
     * Copies all relevant properties from <code>data</code> to <code>obj</code> if supplied or a new instance if not.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @param {module:model/CostSettings} obj Optional instance to populate.
     * @return {module:model/CostSettings} The populated <code>CostSettings</code> instance.
     */
    static constructFromObject(data, obj) {
        if (data) {
            obj = obj || new CostSettings();

            if (data.hasOwnProperty('costMetric')) {
                obj['costMetric'] = ApiClient.convertToType(data['costMetric'], 'String');
            }
            if (data.hasOwnProperty('costMultiplier')) {
                obj['costMultiplier'] = CostMultiplier.constructFromObject(data['costMultiplier']);
            }
            if (data.hasOwnProperty('costAdder')) {
                obj['costAdder'] = CostAdder.constructFromObject(data['costAdder']);
            }
            if (data.hasOwnProperty('autoCalculateDuration')) {
                obj['autoCalculateDuration'] = ApiClient.convertToType(data['autoCalculateDuration'], 'Boolean');
            }
            if (data.hasOwnProperty('autoCalculateDistance')) {
                obj['autoCalculateDistance'] = ApiClient.convertToType(data['autoCalculateDistance'], 'Boolean');
            }
            if (data.hasOwnProperty('distanceMethod')) {
                obj['distanceMethod'] = ApiClient.convertToType(data['distanceMethod'], 'String');
            }
            if (data.hasOwnProperty('durationMethod')) {
                obj['durationMethod'] = ApiClient.convertToType(data['durationMethod'], 'String');
            }
            if (data.hasOwnProperty('averageSpeed')) {
                obj['averageSpeed'] = ApiClient.convertToType(data['averageSpeed'], 'Number');
            }
            if (data.hasOwnProperty('roadCircuityFactor')) {
                obj['roadCircuityFactor'] = ApiClient.convertToType(data['roadCircuityFactor'], 'Number');
            }
        }
        return obj;
    }

    /**
     * Validates the JSON data with respect to <code>CostSettings</code>.
     * @param {Object} data The plain JavaScript object bearing properties of interest.
     * @return {boolean} to indicate whether the JSON data is valid with respect to <code>CostSettings</code>.
     */
    static validateJSON(data) {
        // check to make sure all required properties are present in the JSON string
        for (const property of CostSettings.RequiredProperties) {
            if (!data.hasOwnProperty(property)) {
                throw new Error("The required field `" + property + "` is not found in the JSON data: " + JSON.stringify(data));
            }
        }
        // ensure the json data is a string
        if (data['costMetric'] && !(typeof data['costMetric'] === 'string' || data['costMetric'] instanceof String)) {
            throw new Error("Expected the field `costMetric` to be a primitive type in the JSON string but got " + data['costMetric']);
        }
        // validate the optional field `costMultiplier`
        if (data['costMultiplier']) { // data not null
          CostMultiplier.validateJSON(data['costMultiplier']);
        }
        // validate the optional field `costAdder`
        if (data['costAdder']) { // data not null
          CostAdder.validateJSON(data['costAdder']);
        }
        // ensure the json data is a string
        if (data['distanceMethod'] && !(typeof data['distanceMethod'] === 'string' || data['distanceMethod'] instanceof String)) {
            throw new Error("Expected the field `distanceMethod` to be a primitive type in the JSON string but got " + data['distanceMethod']);
        }
        // ensure the json data is a string
        if (data['durationMethod'] && !(typeof data['durationMethod'] === 'string' || data['durationMethod'] instanceof String)) {
            throw new Error("Expected the field `durationMethod` to be a primitive type in the JSON string but got " + data['durationMethod']);
        }

        return true;
    }


}

CostSettings.RequiredProperties = ["costMetric"];

/**
 * The metric to use as the base for calculating travel costs. A multiplier and/or adder can be applied to this metric to calculate the final travel cost. If duration is selected, then the travel cost will be calculated based on the travel duration between locations. If distance is selected, then the travel cost will be calculated based on the travel distance between locations.
 * @member {module:model/CostSettings.CostMetricEnum} costMetric
 * @default 'duration'
 */
CostSettings.prototype['costMetric'] = 'duration';

/**
 * @member {module:model/CostMultiplier} costMultiplier
 */
CostSettings.prototype['costMultiplier'] = undefined;

/**
 * @member {module:model/CostAdder} costAdder
 */
CostSettings.prototype['costAdder'] = undefined;

/**
 * Whether to automatically calculate travel durations between locations based on their latitude and longitude.
 * @member {Boolean} autoCalculateDuration
 * @default false
 */
CostSettings.prototype['autoCalculateDuration'] = false;

/**
 * Whether to automatically calculate travel distances between locations based on their latitude and longitude.
 * @member {Boolean} autoCalculateDistance
 * @default false
 */
CostSettings.prototype['autoCalculateDistance'] = false;

/**
 * The method to use for calculating distances between locations. Driving uses Open Route Service to calculate driving distances, geo uses the Haversine formula to calculate distances based on latitude and longitude, and Manhattan uses the Manhattan distance formula.
 * @member {module:model/CostSettings.DistanceMethodEnum} distanceMethod
 * @default 'geo'
 */
CostSettings.prototype['distanceMethod'] = 'geo';

/**
 * The method to use for calculating durations between locations. Driving uses Open Route Service to calculate driving durations, geo uses the Haversine formula to calculate durations based on latitude and longitude, and Manhattan uses the Manhattan distance formula.
 * @member {module:model/CostSettings.DurationMethodEnum} durationMethod
 * @default 'geo'
 */
CostSettings.prototype['durationMethod'] = 'geo';

/**
 * Average travel speed, in distance units per hour, used to turn a computed distance into a
 * duration for the non-driving methods (geo, manhattan, euclidean). Ignored for driving. The
 * backend defaults it to 30 when absent.
 * @member {Number} averageSpeed
 */
CostSettings.prototype['averageSpeed'] = undefined;

/**
 * Multiplier applied to a straight-line distance when deriving a duration, for roads that don't run
 * as the crow flies. Duration only. The backend defaults it to 1.25 when absent.
 * @member {Number} roadCircuityFactor
 */
CostSettings.prototype['roadCircuityFactor'] = undefined;





/**
 * Allowed values for the <code>costMetric</code> property.
 * @enum {String}
 * @readonly
 */
CostSettings['CostMetricEnum'] = {

    /**
     * value: "duration"
     * @const
     */
    "duration": "duration",

    /**
     * value: "distance"
     * @const
     */
    "distance": "distance",

    /**
     * value: "custom"
     * @const
     */
    "custom": "custom"
};


/**
 * Allowed values for the <code>distanceMethod</code> property.
 * @enum {String}
 * @readonly
 */
CostSettings['DistanceMethodEnum'] = {

    /**
     * value: "driving"
     * @const
     */
    "driving": "driving",

    /**
     * value: "geo"
     * @const
     */
    "geo": "geo",

    /**
     * value: "manhattan"
     * @const
     */
    "manhattan": "manhattan",

    /**
     * value: "euclidean"
     * @const
     */
    "euclidean": "euclidean"
};


/**
 * Allowed values for the <code>durationMethod</code> property.
 * @enum {String}
 * @readonly
 */
CostSettings['DurationMethodEnum'] = {

    /**
     * value: "driving"
     * @const
     */
    "driving": "driving",

    /**
     * value: "geo"
     * @const
     */
    "geo": "geo",

    /**
     * value: "manhattan"
     * @const
     */
    "manhattan": "manhattan",

    /**
     * value: "euclidean"
     * @const
     */
    "euclidean": "euclidean"
};



export default CostSettings;

